/**
 * One anonymous sample of how long the app took to load, and how much JS/CSS that cost.
 *
 * Client half of #652. Fire-and-forget: it must never delay or break the app. One sample per
 * browser session, sent with `sendBeacon` so a page being closed still reports it. No user or
 * session id is sent - it is anonymous, like the page view counter.
 */

const REPORT_TIMEOUT_MS = 8000
const SENT_KEY = 'hourglass.loadMetric.sent'

/** The route a load happened on - coarse on purpose, so a URL carrying an id is never sent. */
export function pageLabel(pathname) {
  const path = pathname || '/'

  if (path.startsWith('/login')) {
    return 'login'
  }

  if (path.startsWith('/forgotPassword')) {
    return 'forgotPassword'
  }

  return 'app'
}

/**
 * Sums the decoded size of the JS and CSS the browser fetched.
 *
 * `decodedBodySize` rather than `transferSize`: transferSize is 0 for anything served from cache
 * (and for cross-origin resources without Timing-Allow-Origin), which would make a warm load look
 * free. Decoded size is the bundle the browser actually parsed, cache or not. Our own assets are
 * same-origin, so it is populated.
 */
export function resourceBytes(entries) {
  return (entries || []).reduce(
    (totals, entry) => {
      const name = entry.name || ''
      const size = entry.decodedBodySize || entry.transferSize || 0

      if (/\.js(\?|$)/.test(name)) {
        totals.jsBytes += size
      } else if (/\.css(\?|$)/.test(name)) {
        totals.cssBytes += size
      }

      return totals
    },
    { jsBytes: 0, cssBytes: 0 }
  )
}

/** The three durations from the navigation entry, relative to navigationStart, rounded to ms. */
export function navigationTimings(navigation) {
  if (!navigation) {
    return {}
  }

  const ms = value => (typeof value === 'number' && isFinite(value) && value > 0 ? Math.round(value) : undefined)

  return {
    ttfbMs: ms(navigation.responseStart - navigation.startTime),
    domMs: ms(navigation.domContentLoadedEventEnd - navigation.startTime),
    loadMs: ms(navigation.loadEventEnd - navigation.startTime),
  }
}

let latestLcp

function observeLcp() {
  if (typeof PerformanceObserver === 'undefined') {
    return
  }

  try {
    const observer = new PerformanceObserver(list => {
      const entries = list.getEntries()
      if (entries.length) {
        latestLcp = entries[entries.length - 1].startTime
      }
    })
    observer.observe({ type: 'largest-contentful-paint', buffered: true })
  } catch (err) {
    // Not supported - lcpMs is simply omitted from the sample.
  }
}

export function collect() {
  const navigation =
    (typeof performance !== 'undefined' &&
      performance.getEntriesByType &&
      performance.getEntriesByType('navigation')[0]) ||
    undefined
  const resources =
    (typeof performance !== 'undefined' && performance.getEntriesByType && performance.getEntriesByType('resource')) ||
    []
  const sizes = resourceBytes(resources)

  return {
    page: pageLabel(window.location.pathname),
    ...navigationTimings(navigation),
    lcpMs: latestLcp ? Math.round(latestLcp) : undefined,
    ...sizes,
  }
}

function send(payload) {
  const body = new Blob([JSON.stringify(payload)], { type: 'application/json' })

  if (navigator.sendBeacon) {
    navigator.sendBeacon('/api/log/load', body)
    return
  }

  fetch('/api/log/load', {
    method: 'POST',
    body,
    keepalive: true,
    headers: { 'Content-Type': 'application/json' },
  }).catch(() => {})
}

export function report() {
  // One sample per browser session - that *is* the sampling. sessionStorage can throw (private
  // mode); in that case report anyway rather than dropping the metric, and accept the duplicate.
  try {
    if (sessionStorage.getItem(SENT_KEY)) {
      return
    }
    sessionStorage.setItem(SENT_KEY, '1')
  } catch (err) {
    // storage unavailable
  }

  if (typeof performance === 'undefined' || typeof window === 'undefined') {
    return
  }

  observeLcp()

  let sent = false
  const once = () => {
    if (sent) {
      return
    }
    sent = true

    try {
      send(collect())
    } catch (err) {
      // A metric must never break the app.
    }
  }

  // LCP is only final once the page is hidden or the user interacts, so prefer pagehide. The
  // timeout is the fallback for a tab that is never closed within the session.
  window.addEventListener('pagehide', once)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      once()
    }
  })
  setTimeout(once, REPORT_TIMEOUT_MS)
}
